import { useMemo, useState } from 'react';
import InfoIcon from '@assets/icons/tooltip-icon.svg';
import { BaseChart, BaseTooltip, TabBar } from '@components';
import { TEST_IDS } from '@constants/testIds.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import { LEARNING_STATISTICS_MODE } from '@routes/sidebar/___shared___/components/learning-statistics/constants.ts';
import {
    SHARED_LEARNING_STATISTICS_ASHORE,
    SHARED_LEARNING_STATISTICS_COMPLETED,
    SHARED_LEARNING_STATISTICS_MONTHLY_PROGRESS,
    SHARED_LEARNING_STATISTICS_ONBOARD,
    SHARED_LEARNING_STATISTICS_THIS_MONTH,
    SHARED_LEARNING_STATISTICS_THIS_WEEK,
} from '@routes/sidebar/___shared___/components/learning-statistics/constants.ts';
import {
    createBarSeries,
    getLearningStatisticsContent,
} from '@routes/sidebar/___shared___/components/learning-statistics/helpers.ts';
import {
    BottomPart,
    CHARTS_COLORS,
    Completed,
    CompletedLabel,
    CompletedValue,
    Container,
    Graph,
    LeftPart,
    Legend,
    LegendColor,
    LegendItem,
    LegendLabel,
    ProgressBarContainer,
    RightPart,
    StatBox,
    StatLabel,
    StatValue,
    StatWrapper,
    Subtitle,
    TopPart,
    WeekMonth,
} from '@routes/sidebar/___shared___/components/learning-statistics/styled.ts';
import { Title } from '@routes/sidebar/learning/ui/dashboard-tab/___shared___/styled.tsx';
import { selectServerTimeDate } from '@utils/serverTime.ts';
import { translateItem } from '@utils/translations.ts';

import type { LearningStatisticsOptions } from './types.ts';
import { getLearningStatisticsViewModel, getStatisticsSummaryByMode } from './utils.ts';

interface Props {
    options: LearningStatisticsOptions;
}

export const LearningStatistics = ({ options }: Props) => {
    const { t } = useLingui();
    const TAB_BAR_OPTIONS = [t`Time`, t`Elements`, t`Pathways`];
    const serverTimeDateStore = useAppSelector(selectServerTimeDate);

    const [selectedTabSection, setSelectedTabSection] = useState(0);

    const COMPLETED = translateItem(SHARED_LEARNING_STATISTICS_COMPLETED);

    const serverTimeDate = useMemo(() => serverTimeDateStore ?? new Date(), [serverTimeDateStore]);
    const content = getLearningStatisticsContent(options.mode);
    const isLearnerMode = options.mode === LEARNING_STATISTICS_MODE.LEARNER;
    const selectedStatisticsSummary = useMemo(() => getStatisticsSummaryByMode(options), [options]);

    const viewModel = useMemo(
        () =>
            getLearningStatisticsViewModel({
                mode: options.mode,
                statisticsSummary: selectedStatisticsSummary,
                serverTimeDate,
                selectedTabSection,
                completedTextTranslated: COMPLETED,
            }),
        [options.mode, selectedStatisticsSummary, serverTimeDate, selectedTabSection],
    );

    const BaseChartOptions = useMemo(() => {
        const ashoreStyled = viewModel.ashoreData.map((ashoreValue, index) => ({
            value: ashoreValue,
            itemStyle: {
                borderRadius: viewModel.onboardData[index] > 0 ? [0, 0, 0, 0] : [8, 8, 0, 0],
            },
        }));

        const onboardStyled = viewModel.onboardData.map((onboardValue) => ({
            value: onboardValue,
            itemStyle: {
                borderRadius: onboardValue > 0 ? [8, 8, 0, 0] : [0, 0, 0, 0],
            },
        }));

        const completedStyled = viewModel.completedData.map((completedValue) => ({
            value: completedValue,
            itemStyle: {
                borderRadius: completedValue > 0 ? [8, 8, 0, 0] : [0, 0, 0, 0],
            },
        }));

        const series = isLearnerMode
            ? [
                  createBarSeries({
                      name: 'Ashore',
                      data: ashoreStyled,
                      color: CHARTS_COLORS.ashore,
                      stack: 'total',
                      barGap: 20,
                  }),
                  createBarSeries({
                      name: 'Onboard',
                      data: onboardStyled,
                      color: CHARTS_COLORS.completed,
                      stack: 'total',
                  }),
              ]
            : [
                  createBarSeries({
                      name: 'Completed',
                      data: completedStyled,
                      color: CHARTS_COLORS.completed,
                  }),
              ];

        return {
            grid: {
                left: 0,
                right: 0,
                top: 20,
                bottom: 0,
                height: 120,
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: viewModel.last6Months,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { show: true },
            },
            yAxis: {
                show: false,
            },
            series,
        };
    }, [isLearnerMode, viewModel]);

    return (
        <Container data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS}>
            <LeftPart>
                <TopPart>
                    <Title data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_TITLE}>
                        {translateItem(content.title)}
                        {!!content.tooltip && (
                            <BaseTooltip
                                title={<TooltipContent>{translateItem(content.tooltip)}</TooltipContent>}
                                placement="right"
                                arrow={false}
                                icon={<InfoIcon />}
                            />
                        )}
                    </Title>
                    <TabBar
                        tabs={TAB_BAR_OPTIONS}
                        onTabClick={setSelectedTabSection}
                        selectedTabIndex={selectedTabSection}
                    />
                </TopPart>
                <BottomPart>
                    <ProgressBarContainer>
                        <Completed>
                            <CompletedValue data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_COMPLETED_VALUE}>
                                {viewModel.completedVsTotalText}
                            </CompletedValue>
                            <CompletedLabel data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_COMPLETED_LABEL}>
                                {viewModel.text}
                            </CompletedLabel>
                        </Completed>
                    </ProgressBarContainer>
                    <WeekMonth>
                        <StatBox>
                            <StatWrapper>
                                <StatValue data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_WEEK_VALUE}>
                                    {viewModel.weekCompletedText}
                                </StatValue>
                                <StatLabel>{translateItem(SHARED_LEARNING_STATISTICS_THIS_WEEK)}</StatLabel>
                            </StatWrapper>
                        </StatBox>
                        <StatBox>
                            <StatWrapper>
                                <StatValue data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_MONTH_VALUE}>
                                    {viewModel.monthCompletedText}
                                </StatValue>
                                <StatLabel>{translateItem(SHARED_LEARNING_STATISTICS_THIS_MONTH)}</StatLabel>
                            </StatWrapper>
                        </StatBox>
                    </WeekMonth>
                </BottomPart>
            </LeftPart>
            <RightPart>
                <Subtitle>{translateItem(SHARED_LEARNING_STATISTICS_MONTHLY_PROGRESS)}</Subtitle>
                <Graph data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_GRAPH}>
                    <BaseChart option={BaseChartOptions} width="100%" height="140px" />
                    {!!isLearnerMode && (
                        <Legend data-cy={TEST_IDS.SIDEBAR.LEARNING_STATISTICS_LEGEND}>
                            <LegendItem>
                                <LegendColor $color={CHARTS_COLORS.ashore} />
                                <LegendLabel>{translateItem(SHARED_LEARNING_STATISTICS_ASHORE)}</LegendLabel>
                            </LegendItem>

                            <LegendItem>
                                <LegendColor $color={CHARTS_COLORS.completed} />
                                <LegendLabel>{translateItem(SHARED_LEARNING_STATISTICS_ONBOARD)}</LegendLabel>
                            </LegendItem>
                        </Legend>
                    )}
                </Graph>
            </RightPart>
        </Container>
    );
};
